import * as React from "react";
import { useState, useEffect } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
    Button,
    CircularProgress,
    TextField,
    Switch,
    FormControlLabel,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    FormControl,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EventIcon from "@material-ui/icons/Event";
import SettingsIcon from "@material-ui/icons/Settings";
import ViewCarouselIcon from "@material-ui/icons/ViewCarousel";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPost } from "../getConfig";

const PROMO_BANNER_ENDPOINT = API + "/promo-banner-config";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: theme.spacing(3),
        backgroundColor: "#f5f5f5",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 600,
    },
    accordion: {
        marginBottom: theme.spacing(1),
        "&:before": {
            display: "none",
        },
        overflow: "hidden",
    },
    accordionSummary: {
        backgroundColor: "#fafafa",
        borderBottom: "1px solid #e0e0e0",
    },
    sectionIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    sectionTitle: {
        fontWeight: 500,
    },
    fieldGrid: {
        display: "grid !important",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "32px 24px !important",
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: "1fr",
        },
    },
    textAreaGrid: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    submitButton: {
        marginTop: theme.spacing(3),
    },
    tableContainer: {
        marginBottom: theme.spacing(3),
    },
    statusChip: {
        fontWeight: 500,
    },
    activeChip: {
        backgroundColor: "#4caf50",
        color: "white",
    },
    inactiveChip: {
        backgroundColor: "#9e9e9e",
        color: "white",
    },
}));

// Form types
interface BannerFormData {
    name?: string;
    is_active?: boolean;
    priority?: string;
    start_date?: string;
    end_date?: string;
    target_user_type?: string;
    countdown_datetime?: string;
    show_countdown?: boolean;
    // Banner Config
    banner_bg_color_1?: string;
    banner_bg_color_2?: string;
    banner_bg_image_url?: string;
    banner_icon_url?: string;
    banner_title?: string;
    banner_subtitle?: string;
    banner_badge_text?: string;
    banner_badge_color?: string;
    banner_badge_show_live_dot?: boolean;
    banner_on_click_action?: string;
    banner_on_click_screen_name?: string;
    banner_on_click_url?: string;
    // Page Config - Header
    page_header_subtitle?: string;
    page_header_title?: string;
    page_header_text_color?: string;
    // Page Config - Hero
    page_hero_image_url?: string;
    page_hero_height?: string;
    // Page Config - Countdown
    page_countdown_subtitle?: string;
    page_countdown_event_text?: string;
    // Page Config - Content Block
    page_content_icon_url?: string;
    page_content_title?: string;
    page_content_highlight?: string;
    page_content_description?: string;
    // Page Config - CTAs
    page_cta_primary_text?: string;
    page_cta_primary_action?: string;
    page_cta_primary_bg_color?: string;
    page_cta_primary_text_color?: string;
    page_cta_secondary_text?: string;
    page_cta_secondary_action?: string;
    page_cta_secondary_bg_color?: string;
    page_cta_secondary_text_color?: string;
    // Page Config - Footer
    page_footer_text?: string;
    page_footer_link_text?: string;
    page_footer_link_url?: string;
}

interface BannerRecord {
    id: number;
    name: string;
    is_active: boolean;
    priority: number;
    start_date: string | null;
    end_date: string | null;
    target_user_type: string;
    banner_config: any;
    page_config: any;
    countdown_datetime: string | null;
    show_countdown: boolean;
}

const { Form } = withTypes<BannerFormData>();

// Render helpers
const renderInput = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    label,
    ...props
}: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <TextField
            {...props}
            {...inputProps}
            error={!!(touched && error)}
            helperText={touched && error}
            fullWidth
            variant="outlined"
            size="small"
        />
    </div>
);

const renderSwitch = ({ input, label }: any) => {
    const isChecked = input.checked !== undefined ? input.checked : !!input.value;
    return (
        <FormControlLabel
            control={
                <Switch
                    checked={isChecked}
                    onChange={(event) => {
                        input.onChange(event.target.checked);
                    }}
                    color="primary"
                />
            }
            label={label}
        />
    );
};

const renderSelect = ({ input, label, options, ...props }: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <FormControl fullWidth variant="outlined" size="small">
            <Select {...input} {...props}>
                {options.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    </div>
);

const renderTextArea = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    label,
    ...props
}: any) => (
    <div>
        {label && <Typography variant="caption" style={{ color: '#666', marginBottom: 4, display: 'block' }}>{label}</Typography>}
        <TextField
            {...props}
            {...inputProps}
            error={!!(touched && error)}
            helperText={touched && error}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            rows={3}
        />
    </div>
);

const PromoBannerConfig: React.FC = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(false);
    const [banners, setBanners] = useState<BannerRecord[]>([]);
    const [selectedBanner, setSelectedBanner] = useState<BannerRecord | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [expandedSections, setExpandedSections] = useState<string[]>(["basic"]);
    const [initialValues, setInitialValues] = useState<BannerFormData>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState<BannerRecord | null>(null);

    // Load banners on mount
    useEffect(() => {
        loadBanners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadBanners = async () => {
        setLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.get(PROMO_BANNER_ENDPOINT, config);
            const data = response.data?.data || response.data || [];
            setBanners(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load banners:", error);
            notify("Failed to load banners", { type: "error" });
        }
        setLoading(false);
    };

    const handleAccordionChange = (panel: string) => (_event: any, isExpanded: boolean) => {
        setExpandedSections(
            isExpanded
                ? [...expandedSections, panel]
                : expandedSections.filter((s) => s !== panel)
        );
    };

    const handleCreateNew = () => {
        setSelectedBanner(null);
        setIsCreating(true);
        setIsEditing(true);
        setInitialValues({
            is_active: false,
            priority: "0",
            target_user_type: "all",
            show_countdown: false,
            banner_bg_color_1: "#FF6B9D",
            banner_bg_color_2: "#1E3A5F",
            banner_on_click_action: "OPEN_FULL_PAGE",
            page_header_text_color: "#FFFFFF",
            page_hero_height: "200",
            page_cta_primary_action: "OPEN_REFERRAL_SHARE",
            page_cta_primary_bg_color: "#D63384",
            page_cta_primary_text_color: "#FFFFFF",
            page_cta_secondary_action: "CLOSE",
            page_cta_secondary_bg_color: "transparent",
            page_cta_secondary_text_color: "#FFFFFF",
        });
        setExpandedSections(["basic", "schedule", "banner", "page_header"]);
    };

    const handleEditBanner = (banner: BannerRecord) => {
        setSelectedBanner(banner);
        setIsCreating(false);
        setIsEditing(true);

        // Map banner to form values
        const bannerConfig = banner.banner_config || {};
        const pageConfig = banner.page_config || {};

        setInitialValues({
            name: banner.name,
            is_active: banner.is_active,
            priority: String(banner.priority || 0),
            start_date: banner.start_date ? banner.start_date.substring(0, 16) : "",
            end_date: banner.end_date ? banner.end_date.substring(0, 16) : "",
            target_user_type: banner.target_user_type || "all",
            countdown_datetime: banner.countdown_datetime ? banner.countdown_datetime.substring(0, 16) : "",
            show_countdown: banner.show_countdown,
            // Banner config
            banner_bg_color_1: bannerConfig.background?.colors?.[0] || "#FF6B9D",
            banner_bg_color_2: bannerConfig.background?.colors?.[1] || "#1E3A5F",
            banner_bg_image_url: bannerConfig.background?.image_url || "",
            banner_icon_url: bannerConfig.icon_url || "",
            banner_title: bannerConfig.title || "",
            banner_subtitle: bannerConfig.subtitle || "",
            banner_badge_text: bannerConfig.badge?.text || "",
            banner_badge_color: bannerConfig.badge?.color || "#FFD700",
            banner_badge_show_live_dot: bannerConfig.badge?.show_live_dot || false,
            banner_on_click_action: bannerConfig.on_click?.action || "OPEN_FULL_PAGE",
            banner_on_click_screen_name: bannerConfig.on_click?.screen_name || "",
            banner_on_click_url: bannerConfig.on_click?.url || "",
            // Page config - Header
            page_header_subtitle: pageConfig.header?.subtitle || "",
            page_header_title: pageConfig.header?.title || "",
            page_header_text_color: pageConfig.header?.text_color || "#FFFFFF",
            // Page config - Hero
            page_hero_image_url: pageConfig.hero_image?.url || "",
            page_hero_height: String(pageConfig.hero_image?.height || 200),
            // Page config - Countdown
            page_countdown_subtitle: pageConfig.countdown?.subtitle || "",
            page_countdown_event_text: pageConfig.countdown?.event_text || "",
            // Page config - Content Block
            page_content_icon_url: pageConfig.content_blocks?.[0]?.icon_url || "",
            page_content_title: pageConfig.content_blocks?.[0]?.title || "",
            page_content_highlight: pageConfig.content_blocks?.[0]?.highlight || "",
            page_content_description: pageConfig.content_blocks?.[0]?.description || "",
            // Page config - CTAs
            page_cta_primary_text: pageConfig.cta_primary?.text || "",
            page_cta_primary_action: pageConfig.cta_primary?.action || "OPEN_REFERRAL_SHARE",
            page_cta_primary_bg_color: pageConfig.cta_primary?.background_color || "#D63384",
            page_cta_primary_text_color: pageConfig.cta_primary?.text_color || "#FFFFFF",
            page_cta_secondary_text: pageConfig.cta_secondary?.text || "",
            page_cta_secondary_action: pageConfig.cta_secondary?.action || "CLOSE",
            page_cta_secondary_bg_color: pageConfig.cta_secondary?.background_color || "transparent",
            page_cta_secondary_text_color: pageConfig.cta_secondary?.text_color || "#FFFFFF",
            // Page config - Footer
            page_footer_text: pageConfig.footer?.text || "",
            page_footer_link_text: pageConfig.footer?.link_text || "",
            page_footer_link_url: pageConfig.footer?.link_url || "",
        });
        setExpandedSections(["basic", "schedule"]);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setIsCreating(false);
        setSelectedBanner(null);
    };

    const handleDeleteClick = (banner: BannerRecord) => {
        setBannerToDelete(banner);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!bannerToDelete) return;

        try {
            const headers = getConfigPost();
            await axios.delete(`${PROMO_BANNER_ENDPOINT}/${bannerToDelete.id}`, { headers });
            notify("Banner deleted successfully", { type: "success" });
            setDeleteDialogOpen(false);
            setBannerToDelete(null);
            loadBanners();
        } catch (error) {
            console.error("Failed to delete banner:", error);
            notify("Failed to delete banner", { type: "error" });
        }
    };

    const handleSubmit = async (values: BannerFormData) => {
        setLoading(true);

        // Build banner_config JSON
        const bannerConfig = {
            background: {
                type: values.banner_bg_image_url ? "image" : "gradient",
                colors: [values.banner_bg_color_1 || "#FF6B9D", values.banner_bg_color_2 || "#1E3A5F"],
                direction: "vertical",
                image_url: values.banner_bg_image_url || null,
            },
            icon_url: values.banner_icon_url || null,
            title: values.banner_title || "",
            subtitle: values.banner_subtitle || "",
            badge: values.banner_badge_text ? {
                text: values.banner_badge_text,
                color: values.banner_badge_color || "#FFD700",
                show_live_dot: values.banner_badge_show_live_dot || false,
            } : null,
            on_click: {
                action: values.banner_on_click_action || "OPEN_FULL_PAGE",
                screen_name: values.banner_on_click_screen_name || null,
                url: values.banner_on_click_url || null,
            },
        };

        // Build page_config JSON
        const pageConfig = {
            background: {
                type: "gradient",
                colors: [values.banner_bg_color_1 || "#FF6B9D", values.banner_bg_color_2 || "#1E3A5F"],
                direction: "vertical",
            },
            header: {
                subtitle: values.page_header_subtitle || "",
                title: values.page_header_title || "",
                text_color: values.page_header_text_color || "#FFFFFF",
            },
            hero_image: values.page_hero_image_url ? {
                url: values.page_hero_image_url,
                height: parseInt(values.page_hero_height || "200"),
            } : null,
            countdown: {
                show: values.show_countdown,
                subtitle: values.page_countdown_subtitle || "",
                event_text: values.page_countdown_event_text || "",
            },
            content_blocks: values.page_content_highlight ? [{
                type: "icon_text",
                icon_url: values.page_content_icon_url || null,
                title: values.page_content_title || "",
                highlight: values.page_content_highlight || "",
                description: values.page_content_description || "",
            }] : [],
            cta_primary: {
                text: values.page_cta_primary_text || "",
                action: values.page_cta_primary_action || "OPEN_REFERRAL_SHARE",
                background_color: values.page_cta_primary_bg_color || "#D63384",
                text_color: values.page_cta_primary_text_color || "#FFFFFF",
            },
            cta_secondary: values.page_cta_secondary_text ? {
                text: values.page_cta_secondary_text || "",
                action: values.page_cta_secondary_action || "CLOSE",
                background_color: values.page_cta_secondary_bg_color || "transparent",
                text_color: values.page_cta_secondary_text_color || "#FFFFFF",
            } : null,
            footer: values.page_footer_text || values.page_footer_link_text ? {
                text: values.page_footer_text || "",
                link_text: values.page_footer_link_text || "",
                link_url: values.page_footer_link_url || "",
            } : null,
        };

        const payload = {
            name: values.name,
            is_active: values.is_active || false,
            priority: parseInt(values.priority || "0"),
            start_date: values.start_date || null,
            end_date: values.end_date || null,
            target_user_type: values.target_user_type || "all",
            countdown_datetime: values.countdown_datetime || null,
            show_countdown: values.show_countdown || false,
            banner_config: bannerConfig,
            page_config: pageConfig,
        };

        try {
            const headers = getConfigPost();

            if (isCreating) {
                await axios.post(PROMO_BANNER_ENDPOINT, payload, { headers });
                notify("Banner created successfully!", { type: "success" });
            } else if (selectedBanner) {
                await axios.patch(`${PROMO_BANNER_ENDPOINT}/${selectedBanner.id}`, payload, { headers });
                notify("Banner updated successfully!", { type: "success" });
            }

            setIsEditing(false);
            setIsCreating(false);
            setSelectedBanner(null);
            loadBanners();
        } catch (error: any) {
            console.error("Failed to save banner:", error);
            notify(error.response?.data?.message || "Failed to save banner", { type: "error" });
        }

        setLoading(false);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleString();
    };

    // Click action options
    const clickActionOptions = [
        { value: "OPEN_FULL_PAGE", label: "Open Full Page" },
        { value: "OPEN_REFERRAL_SHEET", label: "Open Referral Sheet" },
        { value: "OPEN_MORTGAGE_SHEET", label: "Open Mortgage Sheet" },
        { value: "OPEN_WALLET_TOP_SHEET", label: "Open Wallet Top Sheet" },
        { value: "NAVIGATE", label: "Navigate to Screen" },
        { value: "OPEN_URL", label: "Open URL" },
    ];

    const ctaActionOptions = [
        { value: "OPEN_REFERRAL_SHARE", label: "Open Referral Share" },
        { value: "CLOSE", label: "Close" },
        { value: "OPEN_URL", label: "Open URL" },
        { value: "NAVIGATE", label: "Navigate to Screen" },
        { value: "OPEN_SHOP", label: "Open Shop" },
    ];

    const targetUserOptions = [
        { value: "all", label: "All Users" },
        { value: "sprive_plus", label: "Sprive Plus Only" },
        { value: "non_sprive_plus", label: "Non-Sprive Plus Only" },
        { value: "ambassador", label: "Ambassadors Only" },
    ];

    if (loading && banners.length === 0) {
        return (
            <div className={classes.main} style={{ justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className={classes.main}>
            <Notification />

            <div className={classes.header}>
                <Typography variant="h5" className={classes.title}>
                    Promotional Banners
                </Typography>
                {!isEditing && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        Create New Banner
                    </Button>
                )}
            </div>

            {/* Banner List */}
            {!isEditing && (
                <div className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {banners.map((banner) => (
                                <TableRow key={banner.id}>
                                    <TableCell>{banner.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={banner.is_active ? "Active" : "Inactive"}
                                            size="small"
                                            className={`${classes.statusChip} ${banner.is_active ? classes.activeChip : classes.inactiveChip}`}
                                        />
                                    </TableCell>
                                    <TableCell>{banner.priority}</TableCell>
                                    <TableCell>{formatDate(banner.start_date)}</TableCell>
                                    <TableCell>{formatDate(banner.end_date)}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleEditBanner(banner)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteClick(banner)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {banners.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No banners found. Click "Create New Banner" to add one.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Edit/Create Form */}
            {isEditing && (
                <Form
                    onSubmit={handleSubmit}
                    initialValues={initialValues}
                    render={({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit}>
                            {/* Section 1: Basic Info */}
                            <Accordion
                                expanded={expandedSections.includes("basic")}
                                onChange={handleAccordionChange("basic")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <SettingsIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Basic Info</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="name" component={renderInput} label="Banner Name" placeholder="Dragon's Den Jan 2025" />
                                        <Field name="priority" component={renderInput} label="Priority" type="number" placeholder="100" />
                                        <Field name="is_active" component={renderSwitch} label="Active" type="checkbox" />
                                        <Field name="target_user_type" component={renderSelect} label="Target Audience" options={targetUserOptions} />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 2: Schedule */}
                            <Accordion
                                expanded={expandedSections.includes("schedule")}
                                onChange={handleAccordionChange("schedule")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <EventIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Schedule & Countdown</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="start_date" component={renderInput} label="Start Date" type="datetime-local" InputLabelProps={{ shrink: true }} />
                                        <Field name="end_date" component={renderInput} label="End Date" type="datetime-local" InputLabelProps={{ shrink: true }} />
                                        <Field name="countdown_datetime" component={renderInput} label="Countdown Target (e.g., event time)" type="datetime-local" InputLabelProps={{ shrink: true }} />
                                        <Field name="show_countdown" component={renderSwitch} label="Show Countdown Timer" type="checkbox" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 3: Home Banner */}
                            <Accordion
                                expanded={expandedSections.includes("banner")}
                                onChange={handleAccordionChange("banner")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <ViewCarouselIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Home Banner</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="banner_bg_color_1" component={renderInput} label="Gradient Color 1" placeholder="#FF6B9D" />
                                        <Field name="banner_bg_color_2" component={renderInput} label="Gradient Color 2" placeholder="#1E3A5F" />
                                        <Field name="banner_bg_image_url" component={renderInput} label="Background Image URL (optional)" placeholder="https://cdn.sprive.com/banner-bg.png" />
                                        <Field name="banner_icon_url" component={renderInput} label="Icon URL" placeholder="https://cdn.sprive.com/icon.png" />
                                        <Field name="banner_title" component={renderInput} label="Banner Title" placeholder="Dragons' Den is coming!" />
                                        <Field name="banner_subtitle" component={renderInput} label="Banner Subtitle" placeholder="Tap to find out more" />
                                        <Field name="banner_badge_text" component={renderInput} label="Badge Text (optional)" placeholder="ON TV" />
                                        <Field name="banner_badge_color" component={renderInput} label="Badge Color" placeholder="#FFD700" />
                                        <Field name="banner_badge_show_live_dot" component={renderSwitch} label="Show Live Dot" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 4: Banner Click Action */}
                            <Accordion
                                expanded={expandedSections.includes("click_action")}
                                onChange={handleAccordionChange("click_action")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <TouchAppIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Banner Click Action</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="banner_on_click_action" component={renderSelect} label="On Click Action" options={clickActionOptions} />
                                        {values.banner_on_click_action === "NAVIGATE" && (
                                            <Field name="banner_on_click_screen_name" component={renderInput} label="Screen Name" placeholder="ReferralScreen" />
                                        )}
                                        {values.banner_on_click_action === "OPEN_URL" && (
                                            <Field name="banner_on_click_url" component={renderInput} label="URL" placeholder="https://sprive.com" />
                                        )}
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 5: Full Page - Header */}
                            <Accordion
                                expanded={expandedSections.includes("page_header")}
                                onChange={handleAccordionChange("page_header")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <FullscreenIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Full Page - Header</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_header_subtitle" component={renderInput} label="Subtitle" placeholder="See Sprive enter the Den" />
                                        <Field name="page_header_title" component={renderInput} label="Title" placeholder="You discovered Sprive before it was cool..." />
                                        <Field name="page_header_text_color" component={renderInput} label="Text Color" placeholder="#FFFFFF" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 6: Full Page - Hero Image */}
                            <Accordion
                                expanded={expandedSections.includes("page_hero")}
                                onChange={handleAccordionChange("page_hero")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <FullscreenIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Full Page - Hero Image</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_hero_image_url" component={renderInput} label="Hero Image URL" placeholder="https://cdn.sprive.com/hero.png" />
                                        <Field name="page_hero_height" component={renderInput} label="Image Height (px)" type="number" placeholder="200" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 7: Full Page - Countdown Text */}
                            <Accordion
                                expanded={expandedSections.includes("page_countdown")}
                                onChange={handleAccordionChange("page_countdown")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <EventIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Full Page - Countdown Section</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_countdown_subtitle" component={renderInput} label="Countdown Subtitle" placeholder="Airing in" />
                                        <Field name="page_countdown_event_text" component={renderInput} label="Event Text" placeholder="Dragons' Den, 8pm, Thurs 28th Jan" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 8: Full Page - Content Block */}
                            <Accordion
                                expanded={expandedSections.includes("page_content")}
                                onChange={handleAccordionChange("page_content")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <FullscreenIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Full Page - Content Block</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_content_icon_url" component={renderInput} label="Icon URL" placeholder="https://cdn.sprive.com/money-bag.png" />
                                        <Field name="page_content_title" component={renderInput} label="Title" placeholder="Earn up to" />
                                        <Field name="page_content_highlight" component={renderInput} label="Highlight Text" placeholder="£1,000" />
                                    </div>
                                    <div className={classes.textAreaGrid}>
                                        <Field name="page_content_description" component={renderTextArea} label="Description" placeholder="towards your mortgage by telling a friend about Dragons' Den." />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 9: Full Page - CTAs */}
                            <Accordion
                                expanded={expandedSections.includes("page_ctas")}
                                onChange={handleAccordionChange("page_ctas")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <TouchAppIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Full Page - CTAs</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="subtitle2" style={{ marginBottom: 16 }}>Primary CTA</Typography>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_cta_primary_text" component={renderInput} label="Button Text" placeholder="Tell a friend" />
                                        <Field name="page_cta_primary_action" component={renderSelect} label="Action" options={ctaActionOptions} />
                                        <Field name="page_cta_primary_bg_color" component={renderInput} label="Background Color" placeholder="#D63384" />
                                        <Field name="page_cta_primary_text_color" component={renderInput} label="Text Color" placeholder="#FFFFFF" />
                                    </div>
                                    <Typography variant="subtitle2" style={{ marginTop: 24, marginBottom: 16 }}>Secondary CTA</Typography>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_cta_secondary_text" component={renderInput} label="Button Text" placeholder="Back" />
                                        <Field name="page_cta_secondary_action" component={renderSelect} label="Action" options={ctaActionOptions} />
                                        <Field name="page_cta_secondary_bg_color" component={renderInput} label="Background Color" placeholder="transparent" />
                                        <Field name="page_cta_secondary_text_color" component={renderInput} label="Text Color" placeholder="#FFFFFF" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Section 10: Full Page - Footer */}
                            <Accordion
                                expanded={expandedSections.includes("page_footer")}
                                onChange={handleAccordionChange("page_footer")}
                                className={classes.accordion}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                                    <FullscreenIcon className={classes.sectionIcon} />
                                    <Typography className={classes.sectionTitle}>Full Page - Footer</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className={classes.fieldGrid}>
                                        <Field name="page_footer_text" component={renderInput} label="Footer Text" placeholder="Limited offer until Dragons' Den airs." />
                                        <Field name="page_footer_link_text" component={renderInput} label="Link Text" placeholder="View terms" />
                                        <Field name="page_footer_link_url" component={renderInput} label="Link URL" placeholder="https://sprive.com/terms" />
                                    </div>
                                </AccordionDetails>
                            </Accordion>

                            {/* Submit/Cancel Buttons */}
                            <div className={classes.submitButton}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                    style={{ marginRight: 16 }}
                                >
                                    {loading ? <CircularProgress size={24} /> : (isCreating ? "Create Banner" : "Save Changes")}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelEdit}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Banner</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete "{bannerToDelete?.name}"? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PromoBannerConfig;
